import { useEffect, useRef, useState, type CSSProperties, type RefObject } from 'react';
import { createPortal } from 'react-dom';
import { NavLink } from 'react-router-dom';

import { useAuth } from '@/app/providers/authContext';
import { Icon, type IconName } from '@/components/ui';
import { useCurrentUserName } from '@/features/organization/queries';
import { THEME_LABELS, THEME_PREFERENCES, useThemePreference } from '@/lib/theme';

import { PROFILE_LABELS, profilesForNavigationItem, type NavigationItemId } from '../navigation';
import { SignOutDialog } from '../SignOutDialog';
import type { StoreSummary } from './useStoreSummary';
import styles from './StoreAccountMenu.module.css';

interface AccountPage {
  readonly to: string;
  readonly label: string;
  readonly icon: IconName;
  /** The route this entry opens, so a profile never sees an entry its guard would refuse. */
  readonly guard: NavigationItemId;
  readonly badge?:
    | { readonly text: string; readonly tone: 'brand' | 'warning' | 'neutral' }
    | undefined;
}

/** The account's pages (Figma D3, M3 "Pedidos e pagamentos"), with what each one is waiting on. */
function accountPages(summary: StoreSummary): AccountPage[] {
  return [
    {
      to: '/pedidos',
      label: 'Pedidos',
      icon: 'receipt',
      guard: 'orders',
      badge:
        summary.openOrderCount > 0
          ? {
              text: `${String(summary.openOrderCount)} ${summary.openOrderCount === 1 ? 'aberto' : 'abertos'}`,
              tone: 'brand',
            }
          : undefined,
    },
    { to: '/pedidos/historico', label: 'Histórico de compras', icon: 'chart', guard: 'orders' },
    {
      to: '/pagamentos',
      label: 'Boletos',
      icon: 'barcode',
      guard: 'payments',
      badge:
        summary.boletosDue.length > 0
          ? { text: `${String(summary.boletosDue.length)} a vencer`, tone: 'warning' }
          : undefined,
    },
    {
      to: '/rastreamento',
      label: 'Entregas',
      icon: 'truck',
      guard: 'tracking',
      badge:
        summary.deliveriesOnTheWay > 0
          ? { text: `${String(summary.deliveriesOnTheWay)} a caminho`, tone: 'neutral' }
          : undefined,
    },
    {
      to: '/contratos',
      label: 'Contrato e prazo de pagamento',
      icon: 'contract',
      guard: 'contracts',
    },
  ];
}

function useVisiblePages(summary: StoreSummary): AccountPage[] {
  const { profile } = useAuth();
  return accountPages(summary).filter(
    (page) => profile !== null && profilesForNavigationItem(page.guard).includes(profile),
  );
}

/** "Maria da Silva Souza" → "MS": first and last word, so a middle name never wins the avatar. */
function initialsOf(name: string): string {
  const words = name.split(/[\s@.]+/u).filter((word) => word.length > 0);
  const first = words[0] ?? '?';
  const last = words.length > 1 ? words[words.length - 1] : undefined;
  return `${first.charAt(0)}${last?.charAt(0) ?? ''}`.toUpperCase();
}

function useIdentity() {
  const { user, profile } = useAuth();
  const fullName = useCurrentUserName();
  const email = user?.email ?? 'Sessão anônima';
  const name = fullName ?? email;
  return {
    name,
    firstName: fullName?.split(/\s+/u)[0],
    email,
    hasName: fullName !== undefined,
    initials: initialsOf(fullName ?? email),
    profileLabel: profile === null ? 'Sem perfil' : PROFILE_LABELS[profile],
  };
}

function useDismiss(open: boolean, close: () => void, root: RefObject<HTMLElement | null>) {
  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: MouseEvent) => {
      if (root.current !== null && !root.current.contains(event.target as Node)) close();
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') close();
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open, close, root]);
}

function ThemeChoice() {
  const [theme, setTheme] = useThemePreference();
  return (
    <div className={styles.themeRow} role="group" aria-label="Aparência">
      <span className={styles.themeLabel} aria-hidden="true">
        Aparência
      </span>
      <div className={styles.themeOptions}>
        {THEME_PREFERENCES.map((option) => (
          <button
            key={option}
            type="button"
            role="menuitemradio"
            aria-checked={theme === option}
            className={styles.themeOption}
            onClick={() => {
              setTheme(option);
            }}
          >
            {THEME_LABELS[option]}
          </button>
        ))}
      </div>
    </div>
  );
}

function UnitBox({ summary, compact = false }: { summary: StoreSummary; compact?: boolean }) {
  if (summary.establishmentName === undefined) return null;
  const count = summary.establishmentCount;
  return (
    <div className={compact ? styles.unitCompact : styles.unit}>
      <Icon name="pin" size={18} className={styles.unitIcon} />
      <div>
        <p className={styles.unitName}>{summary.establishmentName}</p>
        {count > 1 ? (
          <p className={styles.unitNote}>
            {compact
              ? `1 de ${String(count)} unidades`
              : `1 de ${String(count)} unidades · os pedidos saem por esta unidade`}
          </p>
        ) : null}
      </div>
    </div>
  );
}

function PageLink({
  page,
  onNavigate,
  index,
}: {
  page: AccountPage;
  onNavigate: () => void;
  index: number;
}) {
  return (
    <NavLink
      to={page.to}
      end={page.to === '/pedidos'}
      role="menuitem"
      viewTransition
      className={({ isActive }) =>
        [styles.item, isActive ? styles.itemActive : null].filter(Boolean).join(' ')
      }
      style={{ '--i': index } as CSSProperties}
      onClick={onNavigate}
    >
      <Icon name={page.icon} size={18} className={styles.itemIcon} />
      <span className={styles.itemLabel}>{page.label}</span>
      {page.badge !== undefined ? (
        <span className={styles.badge} data-tone={page.badge.tone}>
          {page.badge.text}
        </span>
      ) : null}
    </NavLink>
  );
}

export interface StoreAccountMenuProps {
  readonly summary: StoreSummary;
  /** "Ajuda e atalhos" — opens the ⌘K palette with every place the profile can reach. */
  readonly onHelp: () => void;
}

/**
 * "Olá, Maria · Conta e pedidos" (Figma D3): who is signed in, the unit the orders leave from,
 * the account's pages with what each is waiting on, the appearance, help and signing out.
 *
 * Motion (D3): the veil darkens to 42 %, the menu rises 0,96 → 1 from −6 px (180 ms), its blocks
 * cascade 30 ms and the pages slide 6 px in from the left 25 ms apart. Closing is the reverse.
 */
export function StoreAccountMenu({ summary, onHelp }: StoreAccountMenuProps) {
  const identity = useIdentity();
  const pages = useVisiblePages(summary);
  const [open, setOpen] = useState(false);
  const [confirmingSignOut, setConfirmingSignOut] = useState(false);
  const rootRef = useRef<HTMLDivElement | null>(null);
  const close = useRef(() => {
    setOpen(false);
  }).current;
  useDismiss(open, close, rootRef);

  return (
    <div className={styles.root} ref={rootRef}>
      <button
        type="button"
        className={styles.trigger}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={`Conta: ${identity.name}, ${identity.profileLabel}`}
        onClick={() => {
          setOpen((value) => !value);
        }}
      >
        <span className={styles.avatar} aria-hidden="true">
          {identity.initials}
        </span>
        <span className={styles.triggerText} aria-hidden="true">
          <span className={styles.triggerHello}>
            {identity.firstName !== undefined ? `Olá, ${identity.firstName}` : 'Olá'}
          </span>
          <span className={styles.triggerTitle}>
            Conta e pedidos
            <Icon name="chevronDown" size={14} strokeWidth={2} />
          </span>
        </span>
      </button>

      {open
        ? createPortal(<div className={styles.veil} aria-hidden="true" />, document.body)
        : null}

      {open ? (
        <div className={styles.menu} role="menu" aria-label="Conta e pedidos">
          <div className={styles.block} style={{ '--b': 0 } as CSSProperties}>
            <div className={styles.who}>
              <span className={styles.avatarLarge} aria-hidden="true">
                {identity.initials}
              </span>
              <div>
                <p className={styles.whoName}>{identity.name}</p>
                {identity.hasName ? <p className={styles.whoEmail}>{identity.email}</p> : null}
                <p className={styles.whoProfile}>{identity.profileLabel}</p>
              </div>
            </div>
            <UnitBox summary={summary} />
          </div>
          <div className={styles.block} style={{ '--b': 1 } as CSSProperties}>
            {pages.map((page, index) => (
              <PageLink key={page.to} page={page} index={index} onNavigate={close} />
            ))}
            <PageLink
              page={{
                to: '/minha-organizacao',
                label: 'Minha organização',
                icon: 'building',
                guard: 'organization',
              }}
              index={pages.length}
              onNavigate={close}
            />
          </div>
          <div className={styles.block} style={{ '--b': 2 } as CSSProperties}>
            <ThemeChoice />
          </div>
          <div className={styles.block} style={{ '--b': 3 } as CSSProperties}>
            <button
              type="button"
              role="menuitem"
              className={styles.item}
              onClick={() => {
                close();
                onHelp();
              }}
            >
              <Icon name="help" size={18} className={styles.itemIcon} />
              <span className={styles.itemLabel}>Ajuda e atalhos</span>
              <kbd className={styles.kbd}>⌘K</kbd>
            </button>
            <button
              type="button"
              role="menuitem"
              className={styles.item}
              onClick={() => {
                close();
                setConfirmingSignOut(true);
              }}
            >
              <Icon name="logout" size={18} className={styles.itemIcon} />
              <span className={styles.itemLabel}>Sair</span>
            </button>
          </div>
        </div>
      ) : null}

      <SignOutDialog
        open={confirmingSignOut}
        onClose={() => {
          setConfirmingSignOut(false);
        }}
      />
    </div>
  );
}

export interface StoreDrawerProps {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly summary: StoreSummary;
  readonly onHelp: () => void;
}

/**
 * The phone's side drawer (Figma M3), in place of the account menu: it also carries the buying
 * shortcuts, so the phone has one door to everything. Slides in from the left (−330 px → 0,
 * 320 ms, drawer) over a 50 % veil; its items cascade 20 ms, 8 px. Closing slides it back.
 */
export function StoreDrawer({ open, onClose, summary, onHelp }: StoreDrawerProps) {
  const identity = useIdentity();
  const pages = useVisiblePages(summary);
  const [confirmingSignOut, setConfirmingSignOut] = useState(false);
  const [leaving, setLeaving] = useState(false);
  const [mounted, setMounted] = useState(open);

  if (open && !mounted) setMounted(true);
  useEffect(() => {
    if (!open && mounted) {
      setLeaving(true);
      const timer = window.setTimeout(() => {
        setLeaving(false);
        setMounted(false);
      }, 200);
      return () => {
        window.clearTimeout(timer);
      };
    }
    return undefined;
  }, [open, mounted]);

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open, onClose]);

  const buyLinks: AccountPage[] = [
    { to: '/', label: 'Início', icon: 'home', guard: 'catalog' },
    { to: '/catalogo', label: 'Categorias', icon: 'grid', guard: 'catalog' },
    { to: '/#comprar-de-novo', label: 'Comprar de novo', icon: 'repeat', guard: 'catalog' },
    { to: '/#mais-comprados', label: 'Mais comprados', icon: 'star', guard: 'catalog' },
  ];
  let index = 0;

  return (
    <>
      {mounted
        ? createPortal(
            <div className={styles.drawerLayer} data-leaving={leaving ? '' : undefined}>
              <button
                type="button"
                className={styles.drawerVeil}
                aria-label="Fechar o menu"
                onClick={onClose}
              />
              <nav className={styles.drawer} aria-label="Menu">
                <div className={styles.drawerHead}>
                  <span className={styles.avatarOnBrand} aria-hidden="true">
                    {identity.initials}
                  </span>
                  <div className={styles.drawerWho}>
                    <p className={styles.drawerName}>{identity.name}</p>
                    <p className={styles.drawerProfile}>
                      {[identity.profileLabel, summary.establishmentName]
                        .filter(Boolean)
                        .join(' · ')}
                    </p>
                  </div>
                  <button
                    type="button"
                    className={styles.drawerClose}
                    onClick={onClose}
                    aria-label="Fechar"
                  >
                    <Icon name="close" size={22} />
                  </button>
                </div>
                <UnitBox summary={summary} compact />
                <p className={styles.drawerGroup}>Comprar</p>
                {buyLinks.map((page) => (
                  <PageLink key={page.to} page={page} index={index++} onNavigate={onClose} />
                ))}
                <p className={styles.drawerGroup}>Pedidos e pagamentos</p>
                {pages.map((page) => (
                  <PageLink
                    key={page.to}
                    page={page.to === '/contratos' ? { ...page, label: 'Contrato e prazo' } : page}
                    index={index++}
                    onNavigate={onClose}
                  />
                ))}
                <p className={styles.drawerGroup}>Conta</p>
                <PageLink
                  page={{
                    to: '/minha-organizacao',
                    label: 'Minha organização',
                    icon: 'building',
                    guard: 'organization',
                  }}
                  index={index++}
                  onNavigate={onClose}
                />
                <button
                  type="button"
                  className={styles.item}
                  style={{ '--i': index++ } as CSSProperties}
                  onClick={() => {
                    onClose();
                    onHelp();
                  }}
                >
                  <Icon name="help" size={18} className={styles.itemIcon} />
                  <span className={styles.itemLabel}>Ajuda</span>
                </button>
                <button
                  type="button"
                  className={styles.item}
                  style={{ '--i': index++ } as CSSProperties}
                  onClick={() => {
                    onClose();
                    setConfirmingSignOut(true);
                  }}
                >
                  <Icon name="logout" size={18} className={styles.itemIcon} />
                  <span className={styles.itemLabel}>Sair</span>
                </button>
                <div className={styles.drawerTheme}>
                  <ThemeChoice />
                </div>
              </nav>
            </div>,
            document.body,
          )
        : null}
      <SignOutDialog
        open={confirmingSignOut}
        onClose={() => {
          setConfirmingSignOut(false);
        }}
      />
    </>
  );
}
